/**
 * The relay room's key. A room name is the address of a car's live telemetry, and the server will not
 * open the socket without the key that goes with it. Only the server can compute a key: we ask for the
 * one belonging to our own room, naming the account it hangs off, and keep it next to the account.
 */
const STORE = 'roardrive:roomkeys:v1';
const ACCOUNT = 'roardrive:account:v1';

function readAll(): Record<string, string> {
  try { return JSON.parse(localStorage.getItem(STORE) || '{}') as Record<string, string>; } catch { return {}; }
}
function remember(room: string, key: string): void {
  try { const all = readAll(); all[room] = key; localStorage.setItem(STORE, JSON.stringify(all)); } catch { /* private mode */ }
}
/** the account we are signed in as, if any (read raw: this file must not drag the session in) */
function accountUserId(): string {
  try {
    const a = JSON.parse(localStorage.getItem(ACCOUNT) || 'null') as { userId?: string; room?: string; roomKey?: string } | null;
    return a?.userId ?? '';
  } catch { return ''; }
}
function accountKey(room: string): string {
  try {
    const a = JSON.parse(localStorage.getItem(ACCOUNT) || 'null') as { room?: string; roomKey?: string } | null;
    return a && a.room === room && a.roomKey ? a.roomKey : '';
  } catch { return ''; }
}

export async function relayKeyForRoom(room: string): Promise<string> {
  if (!room) return '';
  const known = accountKey(room) || readAll()[room];
  if (known) return known;
  const userId = accountUserId();
  // an account room is only handed over to whoever can name the account; a hand-made room asks by name
  const body = userId && room.startsWith('u') ? { userId } : { room };
  const r = await fetch('/api/relay/key', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  if (!r.ok) throw new Error(`relay key ${r.status}`);
  const j = (await r.json()) as { room: string; key: string };
  if (j.room === room && j.key) remember(room, j.key);
  return j.room === room ? j.key : '';
}

/** after a sign-in: the account payload already carries the key for its own room */
export function rememberRoomKey(room: string, key: string): void {
  if (room && key) remember(room, key);
}

/** The relay refused this room's key: forget it so the next attempt asks for a fresh one. A key can
 *  stop being valid without anything being broken — the server's signing secret was rotated. */
export function forgetRoomKey(room: string): void {
  try {
    const all = readAll();
    delete all[room];
    localStorage.setItem(STORE, JSON.stringify(all));
    const raw = localStorage.getItem(ACCOUNT);
    if (raw) {
      const a = JSON.parse(raw) as { room?: string; roomKey?: string };
      if (a && a.room === room && a.roomKey) { delete a.roomKey; localStorage.setItem(ACCOUNT, JSON.stringify(a)); }
    }
  } catch { /* private mode */ }
}
